/**
 * `temporal.gap` (ADR-0005 §"Auditor"; #197) — an OPT-IN, low-severity gap
 * detector for `∅`-uniqueness-key predicates: a stretch of in-world time, BETWEEN
 * two asserted segments of the same `(subject, predicate)`, that no segment
 * covers ("no known ruler 1067–1070"). This is editorial hygiene — a "you may
 * have meant to record something here" nudge — never a constraint. It surfaces
 * at `info`.
 *
 * **Opt-in (ADR-0005 §"Auditor").** Gaps are noise for most campaigns (history
 * is naturally sparse), so the rule is SILENT unless the GM explicitly enables
 * it for the campaign — i.e. unless an `auditor_rule_overrides` entry for
 * `temporal.gap` is present and is NOT `'off'`. This reuses the existing
 * per-Campaign severity-override mechanism (§5.2.1) as the enable switch: an
 * absent override means "default", and the default for this rule is OFF. The
 * engine still applies the override to the emitted advisory's severity, so a GM
 * who promotes it to `warning` gets `warning`-level gaps.
 *
 * Scope (deliberately narrow, ADR-0005 §2):
 *  - Only `∅`-key predicates (`uniqueness_key: []`) — "at most one segment
 *    covers any day". A genuine interior gap there is a missing-history signal.
 *    `{object}`/`{role}` keys legitimately have per-value/per-role gaps (a value
 *    simply stops being asserted), so they are out of scope.
 *  - Only INTERIOR gaps — strictly between two asserted segments. The open
 *    head (before the first segment / after the last) is the unknown past/future,
 *    not a gap; flagging it would fire on every single-segment predicate.
 *  - Owner-scoped: canon gaps and belief gaps are computed independently (a
 *    belief timeline having a hole is the believer's incomplete picture, not a
 *    canon gap). Matches the overlap rule's owner-scoping (ADR-0005 §5/§8).
 *
 * Half-open `[from, to)` (ADR-0005 §4): segment A `[…, X)` abutting segment B
 * `[X, …)` leaves NO gap (B owns day X). A gap exists iff `prev.to < next.from`
 * after sorting by `from`; the gap is `[prev.to, next.from)` and `next.from -
 * prev.to` days wide.
 */
import { splitFactKey, uniquenessGroupValue, viewerKey, type FactState } from '@dl/shared';

import { factRef, type RawAdvisory, type SeverityOverride } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** The rule id — used both as the advisory `rule` and the opt-in override key. */
export const TEMPORAL_GAP_RULE = 'temporal.gap';

/** Latest asserted value of a fact chain (respecting soft-delete). */
function currentValue(fact: FactState): unknown {
  if (fact.soft_deleted) return undefined;
  return fact.versions[fact.versions.length - 1]?.value;
}

/** Is the gap rule enabled for this campaign? (Opt-in via a non-`off` override.) */
function gapEnabled(override: SeverityOverride | undefined): boolean {
  return override !== undefined && override !== 'off';
}

interface BoundedSeg {
  factKey: string;
  from: number;
  to: number;
}

export function temporalGap(ctx: AuditContext): RawAdvisory[] {
  // Opt-in gate: the rule is silent unless the GM enabled `temporal.gap`.
  if (!gapEnabled(ctx.overrides?.[TEMPORAL_GAP_RULE])) return [];

  const out: RawAdvisory[] = [];

  for (const entity of Object.values(ctx.entities)) {
    if (entity.soft_deleted) continue;

    // Group live segments by (predicate, owner) — gaps are only meaningful
    // within one author's `∅`-key timeline for one predicate.
    const byKey = new Map<string, BoundedSeg[]>();
    for (const [factKey, fact] of Object.entries(entity.facts)) {
      if (fact.soft_deleted) continue;
      const predicate = splitFactKey(factKey).predicate;
      const decl = ctx.registry.lookup(predicate);
      // Only `∅`-uniqueness-key predicates participate (single coverage). An
      // absent key (non-segmented) or a `{object}`/`{role}` key is out of scope.
      if (!decl || decl.uniqueness_key === undefined) continue;
      if (uniquenessGroupValue(decl.uniqueness_key, currentValue(fact)) !== '') continue;
      const v = fact.validity;
      // A gap is only computable between two BOUNDED segments. A segment with no
      // explicit `from`/`to` is open into the unknown and never forms an
      // interior boundary.
      if (!v || v.from === undefined || v.to === undefined) continue;
      const owner = fact.owner.kind === 'belief' ? viewerKey(fact.owner.viewer) : 'canon';
      const k = predicate + ' ' + owner;
      const list = byKey.get(k);
      const seg: BoundedSeg = { factKey, from: v.from, to: v.to };
      if (list) list.push(seg);
      else byKey.set(k, [seg]);
    }

    for (const [k, segs] of byKey) {
      if (segs.length < 2) continue;
      const predicate = k.slice(0, k.indexOf(' '));
      // Sort by `from` then walk adjacent pairs. A gap exists between two
      // consecutive segments when the earlier one's exclusive `to` is strictly
      // before the later one's inclusive `from` (half-open — abutting is no gap).
      const sorted = [...segs].sort((a, b) => a.from - b.from || a.to - b.to);
      // Track the furthest coverage so far (segments may nest/overlap; an
      // overlap is the cardinality rule's concern, not a gap).
      let coveredTo = sorted[0]!.to;
      for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i]!;
        if (next.from > coveredTo) {
          // Interior gap [coveredTo, next.from). Find the segment that owns the
          // left edge so the advisory references a real, stable pair of facts.
          const prev = sorted
            .filter((s) => s.to === coveredTo)
            .sort((a, b) => a.factKey.localeCompare(b.factKey))[0]!;
          out.push({
            severity: 'info',
            rule: TEMPORAL_GAP_RULE,
            message_key: 'auditor.temporal.gap',
            params: {
              predicate,
              from: coveredTo,
              to: next.from,
              days: next.from - coveredTo,
            },
            fact_ids: [factRef(entity.id, prev.factKey), factRef(entity.id, next.factKey)],
            entity_ids: [entity.id],
          });
        }
        if (next.to > coveredTo) coveredTo = next.to;
      }
    }
  }

  return out;
}
